// src/pages/VerifyReceipt/VerifyReceipt.tsx
import { useState, useEffect, FormEvent, ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Container, Card, Form, Button, InputGroup,
  Spinner, Alert, Row, Col
} from 'react-bootstrap';
import {
  FaShieldAlt, FaCheckCircle, FaExclamationTriangle,
  FaSearch, FaFileInvoice, FaBuilding, FaUser,
  FaCalendarAlt, FaMoneyBillWave, FaHashtag
} from 'react-icons/fa';
import api from '../../services/api';

// ─────────────────────────────────────────
// TIPAGENS
// ─────────────────────────────────────────
interface ReciboData {
  numero:         string;
  cliente:        string;
  clienteCnpj:    string | null;
  plano:          string;
  parcela:        number;
  valor:          number;
  dataVencimento: string;
  dataPagamento:  string;
  emissor:        string;
  emissorCnpj:    string | null;
}

interface ReceiptResponse {
  valid:    boolean;
  message?: string;
  recibo?:  ReciboData;
}

// ─────────────────────────────────────────
// PÁGINA
// ─────────────────────────────────────────
export default function VerifyReceipt() {
  const [searchParams] = useSearchParams();
  const [numero,  setNumero]  = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [result,  setResult]  = useState<ReceiptResponse | null>(null);

  // 🔥 Auto-verifica se vier ?numero=XXXX na URL (do QR Code)
  useEffect(() => {
    const fromUrl = searchParams.get('numero');
    if (fromUrl) {
      setNumero(fromUrl.toUpperCase());
      verify(fromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const verify = async (receiptNumber: string) => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.get<ReceiptResponse>(
        `/public/verify-receipt/${receiptNumber.trim()}`
      );
      setResult(data);
    } catch {
      setResult({
        valid: false,
        message: 'Erro ao verificar recibo. Tente novamente.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!numero.trim()) return;
    verify(numero);
  };

  const formatCurrency = (v: number) =>
    Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  const formatDate = (d: string) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #e6fffa 0%, #ffffff 100%)',
        padding: '3rem 0',
      }}
    >
      <Container style={{ maxWidth: '720px' }}>
        {/* ── Cabeçalho ── */}
        <div className="text-center mb-4">
          <div
            className="d-inline-flex align-items-center justify-content-center mb-3"
            style={{
              width: 72,
              height: 72,
              background: '#0F766E',
              borderRadius: '50%',
              boxShadow: '0 10px 25px -5px rgba(15, 118, 110, 0.4)',
            }}
          >
            <FaShieldAlt size={32} color="#fff" />
          </div>
          <h1 className="fw-bold mb-2" style={{ color: '#1F2937' }}>
            Verificar Recibo
          </h1>
          <p className="text-muted mb-0">
            Digite o número do recibo para validar sua autenticidade.
          </p>
        </div>

        {/* ── Formulário de busca ── */}
        <Card className="shadow-sm mb-4 border-0">
          <Card.Body className="p-4">
            <Form onSubmit={handleSubmit}>
              <Form.Label className="fw-semibold small text-muted">
                NÚMERO DO RECIBO
              </Form.Label>
              <InputGroup>
                <InputGroup.Text style={{ background: '#f8f9fa' }}>
                  <FaHashtag />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  value={numero}
                  onChange={(e) => setNumero(e.target.value.toUpperCase())}
                  placeholder="Ex: E563F436"
                  maxLength={12}
                  style={{
                    fontFamily: 'monospace',
                    fontSize: '1.1rem',
                    letterSpacing: '2px',
                  }}
                  disabled={loading}
                />
                <Button
                  type="submit"
                  variant="success"
                  disabled={loading || !numero.trim()}
                  style={{ minWidth: 140 }}
                >
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Verificando...
                    </>
                  ) : (
                    <>
                      <FaSearch className="me-2" />
                      Verificar
                    </>
                  )}
                </Button>
              </InputGroup>
            </Form>
          </Card.Body>
        </Card>

        {/* ── ✅ Recibo válido ── */}
        {result && result.valid && result.recibo && (
          <Card className="shadow border-0 overflow-hidden">
            {/* Header verde */}
            <div
              style={{
                background: 'linear-gradient(135deg, #22c55e 0%, #0F766E 100%)',
                padding: '1.5rem',
                color: 'white',
              }}
            >
              <div className="d-flex align-items-center gap-3">
                <div
                  className="d-flex align-items-center justify-content-center"
                  style={{
                    width: 56,
                    height: 56,
                    background: 'rgba(255,255,255,0.2)',
                    borderRadius: '50%',
                    flexShrink: 0,
                  }}
                >
                  <FaCheckCircle size={28} />
                </div>
                <div>
                  <h4 className="mb-1 fw-bold">Recibo Válido</h4>
                  <small className="opacity-90">
                    Documento autenticado pelo sistema.
                  </small>
                </div>
              </div>
            </div>

            {/* Corpo com dados */}
            <Card.Body className="p-4">
              <Row className="g-3">
                <InfoItem
                  icon={<FaHashtag />}
                  label="Número do Recibo"
                  value={result.recibo.numero}
                  mono
                />
                <InfoItem
                  icon={<FaUser />}
                  label="Cliente"
                  value={result.recibo.cliente}
                />
                {result.recibo.clienteCnpj && (
                  <InfoItem
                    icon={<FaBuilding />}
                    label="CNPJ do Cliente"
                    value={result.recibo.clienteCnpj}
                    mono
                  />
                )}
                <InfoItem
                  icon={<FaFileInvoice />}
                  label="Plano"
                  value={result.recibo.plano}
                />
                <InfoItem
                  icon={<FaFileInvoice />}
                  label="Parcela"
                  value={`${result.recibo.parcela}`}
                />
                <InfoItem
                  icon={<FaMoneyBillWave />}
                  label="Valor Pago"
                  value={formatCurrency(result.recibo.valor)}
                  highlight
                />
                <InfoItem
                  icon={<FaCalendarAlt />}
                  label="Data do Pagamento"
                  value={formatDate(result.recibo.dataPagamento)}
                />
                <InfoItem
                  icon={<FaBuilding />}
                  label="Emitido por"
                  value={result.recibo.emissor}
                />
              </Row>
            </Card.Body>

            {/* Rodapé de aviso */}
            <div
              className="px-4 py-3 text-center small text-muted"
              style={{ background: '#f8f9fa', borderTop: '1px solid #e9ecef' }}
            >
              <FaExclamationTriangle className="me-2 text-warning" />
              Compare estes dados com os do PDF. Se houver divergência, o documento foi adulterado.
            </div>
          </Card>
        )}

        {/* ── ❌ Recibo inválido ── */}
        {result && !result.valid && (
          <Alert variant="danger" className="d-flex align-items-start gap-3 shadow-sm">
            <FaExclamationTriangle size={24} className="flex-shrink-0 mt-1" />
            <div>
              <Alert.Heading className="h5 mb-1">Recibo não encontrado</Alert.Heading>
              <p className="mb-0">{result.message}</p>
            </div>
          </Alert>
        )}

        {/* ── Rodapé explicativo ── */}
        {!result && !loading && (
          <Card className="mt-4 border-0 bg-light">
            <Card.Body className="py-3 text-center">
              <small className="text-muted">
                🔒 Sistema oficial de verificação de recibos.
                <br />
                Os dados exibidos são obtidos diretamente do banco de dados do emissor.
              </small>
            </Card.Body>
          </Card>
        )}
      </Container>
    </div>
  );
}

// ─────────────────────────────────────────
// Componente auxiliar: linha de informação
// ─────────────────────────────────────────
interface InfoItemProps {
  icon:       ReactNode;
  label:      string;
  value:      string;
  mono?:      boolean;   // ✅ opcional
  highlight?: boolean;   // ✅ opcional
}

function InfoItem({ icon, label, value, mono = false, highlight = false }: InfoItemProps) {
  return (
    <Col xs={12}>
      <div className="d-flex align-items-center justify-content-between py-2 border-bottom">
        <div className="d-flex align-items-center gap-2 text-muted">
          <span style={{ color: '#0F766E' }}>{icon}</span>
          <small className="fw-semibold">{label}</small>
        </div>
        <div
          className="fw-bold text-end"
          style={{
            fontFamily: mono ? 'monospace' : 'inherit',
            fontSize: highlight ? '1.5rem' : '1rem',
            color: highlight ? '#198754' : '#1F2937',
          }}
        >
          {value}
        </div>
      </div>
    </Col>
  );
}
